// controllers/payrollController.js
import apiService from '../services/apiService';
import Payroll from '../models/Payroll';

export const payrollController = {
  /**
   * Get all payroll records with filters
   * @param {Object} filters - { page, limit, status, month, year, employeeId }
   * @returns {Promise<Object>} - { success, data, total, pagination }
   */
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `payroll?${queryParams}` : 'payroll';
      const response = await apiService.get(endpoint);
      
      const items = response.data?.map(item => Payroll.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: response.total || items.length,
        pagination: response.pagination || null
      };
    } catch (error) {
      console.error('Error fetching payroll:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message || 'Failed to fetch payroll records' 
      };
    }
  },

  /**
   * Get single payroll by ID
   * @param {string} id - Payroll ID
   * @returns {Promise<Object>} - { success, data }
   */
  getById: async (id) => {
    try {
      const response = await apiService.get(`payroll/${id}`);
      return { 
        success: true, 
        data: Payroll.fromJSON(response.data) 
      };
    } catch (error) {
      console.error('Error fetching payroll:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message 
      };
    }
  },

  /**
   * Get payroll by employee ID
   * @param {string} employeeId - Employee ID
   * @param {Object} filters - Additional filters
   * @returns {Promise<Object>} - { success, data, total }
   */
  getByEmployeeId: async (employeeId, filters = {}) => {
    try {
      const queryParams = new URLSearchParams({ ...filters, employeeId }).toString();
      const endpoint = `payroll?${queryParams}`;
      const response = await apiService.get(endpoint);
      
      const items = response.data?.map(item => Payroll.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: response.total || items.length 
      };
    } catch (error) {
      console.error('Error fetching employee payroll:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  },

  /**
   * Create new payroll
   * @param {Object} data - Payroll data
   * @returns {Promise<Object>} - { success, data, message }
   */
  create: async (data) => {
    try {
      const payroll = new Payroll(data);
      const validation = payroll.validate();
      
      if (!validation.isValid) {
        return { 
          success: false, 
          errors: validation.errors,
          message: 'Validation failed'
        };
      }
      
      // Auto-calculate net salary
      payroll.netSalary = payroll.calculateNetSalary();
      
      const response = await apiService.post('payroll', payroll.toJSON());
      
      return { 
        success: true, 
        data: Payroll.fromJSON(response.data), 
        message: response.message || 'Payroll created successfully' 
      };
    } catch (error) {
      console.error('Error creating payroll:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to create payroll' 
      };
    }
  },

  /**
   * Update payroll
   * @param {string} id - Payroll ID
   * @param {Object} data - Updated data
   * @returns {Promise<Object>} - { success, data, message }
   */
  update: async (id, data) => {
    try {
      const payroll = new Payroll({ ...data, id });
      const validation = payroll.validate();
      
      if (!validation.isValid) {
        return { 
          success: false, 
          errors: validation.errors 
        };
      }
      
      // Recalculate net salary
      payroll.netSalary = payroll.calculateNetSalary();
      
      const response = await apiService.put(`payroll/${id}`, payroll.toJSON());
      
      return { 
        success: true, 
        data: Payroll.fromJSON(response.data), 
        message: response.message || 'Payroll updated successfully' 
      };
    } catch (error) {
      console.error('Error updating payroll:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to update payroll' 
      };
    }
  },

  /**
   * Delete payroll
   * @param {string} id - Payroll ID
   * @returns {Promise<Object>} - { success, message }
   */
  delete: async (id) => {
    try {
      const response = await apiService.delete(`payroll/${id}`);
      
      return { 
        success: true, 
        message: response.message || 'Payroll deleted successfully' 
      };
    } catch (error) {
      console.error('Error deleting payroll:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to delete payroll' 
      };
    }
  },

  /**
   * Generate payroll for month
   * @param {Object} data - { month, year, employeeIds }
   * @returns {Promise<Object>} - { success, data, message }
   */
  generate: async (data) => {
    try {
      const response = await apiService.post('payroll/generate', data);
      
      return { 
        success: true, 
        data: response.data,
        message: response.message || 'Payroll generated successfully' 
      };
    } catch (error) {
      console.error('Error generating payroll:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to generate payroll' 
      };
    }
  },

  /**
   * Process payment (mark as paid)
   * @param {string} id - Payroll ID
   * @param {Object} data - { paidDate, paymentMethod, remarks }
   * @returns {Promise<Object>} - { success, data, message }
   */
  processPayment: async (id, data = {}) => {
    try {
      const response = await apiService.patch(`payroll/${id}/process`, {
        paidDate: data.paidDate || new Date().toISOString(),
        paymentMethod: data.paymentMethod || 'Bank Transfer',
        remarks: data.remarks || ''
      });
      
      return { 
        success: true, 
        data: Payroll.fromJSON(response.data), 
        message: response.message || 'Payment processed successfully' 
      };
    } catch (error) {
      console.error('Error processing payment:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to process payment' 
      };
    }
  },

  /**
   * Approve payroll (change status to Pending)
   * @param {string} id - Payroll ID
   * @returns {Promise<Object>} - { success, data, message }
   */
  approve: async (id) => {
    try {
      const response = await apiService.patch(`payroll/${id}/approve`);
      
      return { 
        success: true, 
        data: Payroll.fromJSON(response.data), 
        message: response.message || 'Payroll approved' 
      };
    } catch (error) {
      console.error('Error approving payroll:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to approve payroll' 
      };
    }
  },

  /**
   * Get payroll statistics
   * @param {Object} filters - { month, year, department }
   * @returns {Promise<Object>} - { success, data }
   */
  getStatistics: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams 
        ? `payroll/statistics?${queryParams}` 
        : 'payroll/statistics';
      
      const response = await apiService.get(endpoint);
      
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Error fetching statistics:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message 
      };
    }
  },

  /**
   * Get current month payroll
   * @returns {Promise<Object>} - { success, data, total }
   */
  getCurrentMonth: async () => {
    try {
      const now = new Date();
      const month = now.toLocaleString('default', { month: 'long' });
      const year = now.getFullYear();
      
      const response = await apiService.get(`payroll?month=${month}&year=${year}`);
      
      const items = response.data?.map(item => Payroll.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: items.length 
      };
    } catch (error) {
      console.error('Error fetching current month payroll:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  },

  /**
   * Export payroll
   * @param {Object} filters - Export filters
   * @param {string} format - 'csv' | 'excel' | 'pdf'
   * @returns {Promise<Object>} - { success, data }
   */
  export: async (filters = {}, format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format }).toString();
      const response = await apiService.get(`payroll/export?${queryParams}`);
      
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Error exporting payroll:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to export payroll' 
      };
    }
  },

  /**
   * Download payslip
   * @param {string} id - Payroll ID
   * @returns {Promise<Object>} - { success, data }
   */
  downloadPayslip: async (id) => {
    try {
      const response = await apiService.get(`payroll/${id}/payslip`);
      
      return { 
        success: true, 
        data: response.data,
        message: 'Payslip downloaded successfully'
      };
    } catch (error) {
      console.error('Error downloading payslip:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to download payslip' 
      };
    }
  },

  /**
   * Send payslip email
   * @param {string} id - Payroll ID
   * @returns {Promise<Object>} - { success, message }
   */
  sendPayslip: async (id) => {
    try {
      const response = await apiService.post(`payroll/${id}/send-payslip`);
      
      return { 
        success: true, 
        message: response.message || 'Payslip sent successfully' 
      };
    } catch (error) {
      console.error('Error sending payslip:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to send payslip' 
      };
    }
  },

  /**
   * Calculate salary
   * @param {Object} data - { basicSalary, allowances, bonuses, deductions, tax }
   * @returns {Object} - { grossSalary, netSalary }
   */
  calculateSalary: (data) => {
    const payroll = new Payroll(data);
    return {
      grossSalary: payroll.calculateGrossSalary(),
      netSalary: payroll.calculateNetSalary()
    };
  },

  /**
   * Bulk process payments
   * @param {Array} payrollIds - Array of payroll IDs
   * @param {Object} data - { paymentMethod, remarks }
   * @returns {Promise<Object>} - { success, data, message }
   */
  bulkProcess: async (payrollIds, data = {}) => {
    try {
      const response = await apiService.post('payroll/bulk-process', {
        payrollIds,
        ...data
      });
      
      return { 
        success: true, 
        data: response.data,
        message: response.message || 'Bulk payment processed successfully' 
      };
    } catch (error) {
      console.error('Error bulk processing:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to process bulk payments' 
      };
    }
  }
};

export default payrollController;